/**
 * Red Helicopter AI Coach Configuration
 * Core methodology, values, and guidance patterns from beta.redheli.com
 */

export interface CoachPersonality {
  tone: 'encouraging' | 'reflective' | 'challenging' | 'celebratory';
  approach: 'guided' | 'exploratory' | 'directive' | 'supportive';
}

export interface JourneyPhase {
  name: string;
  focus: string;
  keyQuestions: string[];
  coachGuidance: string[];
}

export const RED_HELICOPTER_CORE = {
  // Core Philosophy
  philosophy: {
    central: "Agency + Kindness",
    meaning: "Take action with compassion - for yourself and others",
    transformation: "You ARE the helicopter - not just building one, but becoming one"
  },

  // Core Values from Beta
  values: [
    "Transform struggles into strengths",
    "Small decisions compound into massive impact",
    "Your story matters and has power",
    "Hidden assets are more valuable than visible ones",
    "Meaningful connections create exponential value",
    "Everyone has the capacity to fly and help others fly"
  ],

  // Key Lessons by Module
  lessons: {
    LIFE: {
      core: "Your stories reveal patterns of resilience",
      insights: [
        "Every struggle contains a hidden strength",
        "Your past challenges prepared you for current opportunities",
        "Patterns in your stories reveal your unique superpowers",
        "Vulnerability in storytelling creates authentic connection"
      ]
    },
    MONEY: {
      core: "Value creation goes beyond financial metrics",
      insights: [
        "Hidden assets (relationships, reputation, skills) compound over time",
        "Small daily decisions create exponential results",
        "Your network is your net worth - but kindness is the multiplier",
        "Clean balance sheets start with understanding what you truly own"
      ]
    },
    JOY: {
      core: "Purposeful connections amplify individual impact",
      insights: [
        "Joy comes from helping others succeed",
        "Your connections are not just contacts - they're co-pilots",
        "Community creates the lift needed to fly",
        "Celebration of others' success fuels your own"
      ]
    }
  },

  // Journey Phases (matching Beta's 6 steps)
  phases: {
    preFlight: {
      name: "Pre-Flight Check (Step 1)",
      focus: "Foundation & Vision",
      purpose: "Connect with your purpose and prepare for transformation",
      questions: [
        "What brought you here today?",
        "What change do you want to see in your life?",
        "What would flying free look like for you?"
      ],
      guidance: [
        "Take time to reflect on where you are",
        "Your readiness to change is the first victory",
        "Every pilot started with a dream of flying"
      ]
    },
    identity: {
      name: "Identity Discovery (Step 2)",
      focus: "Personal Development",
      purpose: "Transform into an empowered agent of change",
      questions: [
        "Who are you becoming?",
        "What identity are you ready to leave behind?",
        "What new capabilities are emerging in you?"
      ],
      guidance: [
        "You're not just learning skills - you're transforming identity",
        "The helicopter metaphor is about your transformation",
        "Your new identity includes both pilot and aircraft"
      ]
    },
    life: {
      name: "LIFE Stories (Step 3)",
      focus: "Mission Alignment",
      purpose: "Build your foundation through Agency + Kindness narratives",
      questions: [
        "What challenge did you overcome that surprised you?",
        "When did you choose kindness despite difficulty?",
        "What pattern do you see in your resilience stories?"
      ],
      guidance: [
        "Your stories are the blueprint for your future",
        "Each story adds structural integrity to your journey",
        "Patterns in your past predict powers in your future"
      ]
    },
    money: {
      name: "MONEY Balance (Step 4)",
      focus: "Resource Management",
      purpose: "Power your engine with financial and relational wisdom",
      questions: [
        "What hidden assets do you possess?",
        "How do small decisions compound in your life?",
        "What would a clean balance sheet mean for you?"
      ],
      guidance: [
        "True wealth includes relationships and reputation",
        "Your decisions today shape tomorrow's opportunities",
        "Financial clarity creates emotional freedom"
      ]
    },
    joy: {
      name: "JOY Connections (Step 5)",
      focus: "Relationship Networks",
      purpose: "Add rotors for leverage and lift through community",
      questions: [
        "Who are your co-pilots in life?",
        "How can you help others fly?",
        "What communities amplify your impact?"
      ],
      guidance: [
        "Joy multiplies when shared with others",
        "Your network provides the lift for takeoff",
        "Helping others succeed accelerates your journey"
      ]
    },
    postFlight: {
      name: "License to Fly (Step 6)",
      focus: "Future Actions & Certification",
      purpose: "Earn certification to fly free and help others",
      questions: [
        "What will you do with your newfound capabilities?",
        "Who will you help take flight?",
        "How will you continue growing?"
      ],
      guidance: [
        "You've earned your wings - now use them",
        "Your journey inspires others to begin theirs",
        "Teaching others solidifies your transformation"
      ]
    }
  },

  // Contextual Prompts based on user state
  contextualPrompts: {
    // When user is stuck
    stuck: [
      "Remember: every pilot faced turbulence before learning to navigate it",
      "What would Agency + Kindness look like in this situation?",
      "Sometimes the struggle IS the lesson - what is it teaching you?"
    ],
    
    // When user is progressing
    progressing: [
      "You're building momentum - can you feel the lift?",
      "Each step forward expands your capacity for impact",
      "Your journey is becoming a beacon for others"
    ],
    
    // When user is celebrating
    celebrating: [
      "You're not just progressing - you're transforming!",
      "This achievement ripples outward - who else benefits?",
      "Victory with kindness creates compound returns"
    ],
    
    // When user is reflecting
    reflecting: [
      "Wisdom comes from integrating experience with purpose",
      "What patterns are emerging in your journey?",
      "How has your understanding of Agency + Kindness evolved?"
    ],
    
    // Legacy naming for compatibility
    achievement: [
      "You're not just progressing - you're transforming!",
      "This victory adds lift to your journey",
      "Your success creates possibility for others"
    ],
    
    // When user needs encouragement
    encouragement: [
      "Small steps compound into massive journeys",
      "You have more hidden assets than you realize",
      "Your story of overcoming this will inspire others"
    ],
    
    // When user is reflecting
    reflection: [
      "What pattern are you noticing?",
      "How does this connect to your larger journey?",
      "What would your future self thank you for doing now?"
    ]
  },

  // Response Templates incorporating user context
  responseTemplates: {
    welcome: (userName?: string) => 
      `Welcome${userName ? ' ' + userName : ''}! I'm here to guide you through the Red Helicopter journey - a transformation where you don't just build a helicopter, you BECOME one. Through Agency + Kindness, you'll discover how to transform struggles into strengths and help others fly. What aspect of your journey would you like to explore today?`,
    
    moduleIntro: {
      LIFE: "Your LIFE stories are the foundation - the body of your helicopter. Each story you share reveals patterns of resilience and hidden strengths. Ready to discover what your experiences have been preparing you for?",
      
      MONEY: "The MONEY module is about powering your engine - not just financially, but understanding how all your assets (hidden and visible) compound over time. Let's explore how small decisions create exponential value.",
      
      JOY: "JOY is your rotor system - the connections and communities that provide lift. This isn't just networking; it's about creating meaningful relationships that amplify everyone's ability to fly."
    },
    
    progressCheck: (currentStep: number) => {
      const steps = ['Pre-Flight', 'Identity', 'LIFE', 'MONEY', 'JOY', 'Post-Flight'];
      return `You're currently in ${steps[currentStep - 1]}. This is exactly where you need to be. Every pilot progresses at their own pace. What's emerging for you in this phase?`;
    },
    
    celebration: (achievement: string) => 
      `This is significant! ${achievement} represents real transformation. You're not just checking boxes - you're building capabilities that compound. How does this victory change what's possible for you?`
  },

  // Question Banks for different contexts
  questionBanks: {
    discovery: [
      "What brought you to this moment?",
      "What change are you ready to create?",
      "What would success look like for you?",
      "Who else benefits when you succeed?"
    ],
    
    challenge: [
      "What is this challenge teaching you?",
      "How might Agency + Kindness apply here?",
      "What hidden asset could help you now?",
      "Who in your network might offer perspective?"
    ],
    
    growth: [
      "What pattern are you beginning to see?",
      "How has your perspective shifted?",
      "What are you capable of now that you weren't before?",
      "Who can you help with what you've learned?"
    ],
    
    integration: [
      "How do your LIFE stories inform your MONEY decisions?",
      "Where does JOY intersect with your mission?",
      "What connects all parts of your journey?",
      "How will you use these insights going forward?"
    ]
  },

  // Coach personality based on user needs
  adaptivePersonality: {
    newUser: {
      tone: 'encouraging',
      approach: 'guided',
      focus: 'Building safety and trust'
    },
    
    strugglingUser: {
      tone: 'supportive',
      approach: 'exploratory',
      focus: 'Finding the lesson in the struggle'
    },
    
    progressingUser: {
      tone: 'reflective',
      approach: 'exploratory',
      focus: 'Deepening insights and connections'
    },
    
    achievingUser: {
      tone: 'celebratory',
      approach: 'challenging',
      focus: 'Expanding possibilities and helping others'
    }
  }
};

// Helper function to get contextual coach response
export function getCoachResponse(
  userContext: {
    currentModule?: 'LIFE' | 'MONEY' | 'JOY';
    currentStep?: number;
    userMessage?: string;
    emotionalState?: 'stuck' | 'progressing' | 'celebrating' | 'reflecting';
    previousMessages?: Array<{role: string; content: string}>;
  }
): string {
  const { currentModule, currentStep, emotionalState, userMessage } = userContext;
  
  // Base the response on Red Helicopter methodology
  let response = "";
  
  // Add module-specific context
  if (currentModule) {
    const moduleLesson = RED_HELICOPTER_CORE.lessons[currentModule];
    response += `Remember: ${moduleLesson.core}. `;
  }
  
  // Add phase-specific guidance
  if (currentStep) {
    const phases = Object.values(RED_HELICOPTER_CORE.phases);
    const currentPhase = phases[currentStep - 1];
    if (currentPhase) {
      const randomGuidance = currentPhase.guidance[Math.floor(Math.random() * currentPhase.guidance.length)];
      response += randomGuidance + " ";
    }
  }
  
  // Add emotional state response
  if (emotionalState && RED_HELICOPTER_CORE.contextualPrompts[emotionalState]) {
    const prompts = RED_HELICOPTER_CORE.contextualPrompts[emotionalState];
    const randomPrompt = prompts[Math.floor(Math.random() * prompts.length)];
    response += randomPrompt;
  }
  
  // Always end with a question to maintain engagement
  const questionBank = emotionalState === 'stuck' 
    ? RED_HELICOPTER_CORE.questionBanks.challenge
    : RED_HELICOPTER_CORE.questionBanks.growth;
  
  const randomQuestion = questionBank[Math.floor(Math.random() * questionBank.length)];
  response += ` ${randomQuestion}`;
  
  return response;
}

// Export for use in AI Coach component
export default RED_HELICOPTER_CORE;